import { Button, HistoryIcon, Input } from '@tlx/atlas';
import { useState } from 'react';
import { YearSelect } from '@Page/SalaryTaxCard/component/ActionBar/YearSelect';
import { FetchTaxcardPopupButton } from '@Page/SalaryTaxCard/component/Altinn/FetchTaxcardPopupButton';
import { AltinnData } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { AltinnLoginForm } from '@Page/SalaryTaxCard/component/Modals/LoginForm/AltinnLoginForm';
import { logoutFromAltinn } from '@Page/SalaryTaxCard/hooks/useSalaryTaxCardForReal';
import { CYPRESS_TESTING_TAXCARDS, PREBEN_COMPANYID } from '@Page/SalaryTaxCard/SalaryTaxCardPage';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { getContextId } from '@tlx/astro-shared';
import { useTskContext } from '@Hooks/useTskContext';

interface ActionBarProps {
    showHasQuit: boolean;
    setShowHasQuit?: (x: boolean) => void;
    year: number;
    setYear?: React.Dispatch<React.SetStateAction<number>>;
    latestAllowedYear: number;
    altinn: AltinnData;
    allSelectedEmployees: number[];
    fetchTaxcardsPopupOpen: boolean;
    setFetchTaxcardsPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ActionBar({
    showHasQuit,
    setShowHasQuit,
    year,
    setYear,
    latestAllowedYear,
    altinn,
    allSelectedEmployees,
    fetchTaxcardsPopupOpen,
    setFetchTaxcardsPopupOpen,
}: ActionBarProps) {
    const { showOldPage } = useTskContext();
    const [justLoginOpen, setJustLoginOpen] = useState(false);
    const justLogIn = !altinn.isLoggedInUntil && altinn.isOlderWaitingTaxcardApplications;

    function fetchButton(className?: string) {
        return (
            <>
                {!justLogIn && (
                    <FetchTaxcardPopupButton
                        year={year.toString()}
                        years={yearOptions(latestAllowedYear, new Date().getFullYear() - 1)}
                        altinn={altinn}
                        allSelectedEmployees={allSelectedEmployees}
                        open={fetchTaxcardsPopupOpen}
                        setOpen={setFetchTaxcardsPopupOpen}
                        className={className}
                    />
                )}
                {justLogIn && justLoginOpen && (
                    <AltinnLoginForm open={justLoginOpen} setOpen={setJustLoginOpen} altinn={altinn} />
                )}
                {justLogIn && (
                    <Button
                        onClick={() => setJustLoginOpen(true)}
                        className={className ?? ''}
                        data-testid="fetch-taxcard-button"
                        disabled={altinn.isWaitingForAltinn}
                    >
                        <FormattedMessage id={'text_altinn_login'} />
                    </Button>
                )}
            </>
        );
    }

    function oldPageButton(className?: string) {
        return (
            <Button
                className={className ?? ''}
                variant="secondary"
                onClick={() => window.open(`/execute/eTaxDeductionCard?contextId=${getContextId()}`)}
                data-testid="oldpage-button"
            >
                <FormattedMessage id={'text_change_to_old_page'} />
                <HistoryIcon />
            </Button>
        );
    }

    const yearSelect = (
        <YearSelect
            setYear={setYear}
            initial={year.toString()}
            options={yearOptions(latestAllowedYear, 2019)}
            variant="chevron"
        />
    );
    const hasQuitSlider = <FetchIncludeQuitEmployeePopupSlider open={showHasQuit} setOpen={setShowHasQuit} />;
    return (
        <div>
            <div className="atl-flex atl-mb-24 atl-mt-auto">
                {yearSelect}
                <div className="atl-ml-16 atl-flex atl-justify-center">{hasQuitSlider}</div>
                <div className="atl-ml-auto">
                    {CYPRESS_TESTING_TAXCARDS &&
                        altinn.isLoggedInUntil &&
                        Number(getContextId()) === PREBEN_COMPANYID && (
                            <Button
                                data-testid={'taxcard-logout-from-altinn-button'}
                                onClick={async () => await logoutFromAltinn()}
                            >
                                Logg ut av Altinn
                            </Button>
                        )}
                    {showOldPage && <span className="atl-ml-8">{oldPageButton()}</span>}
                    <span className="atl-ml-8">{fetchButton()}</span>
                </div>
            </div>
        </div>
    );
}

function yearOptions(latestAllowedYear: number, startYear: number) {
    return Array.from({ length: latestAllowedYear - startYear + 1 }, (_, i) => (i + startYear).toString());
}

interface SetStateProps {
    open: boolean;
    setOpen?: (x: boolean) => void;
    className?: string;
}

function FetchIncludeQuitEmployeePopupSlider({ open, setOpen, className }: SetStateProps) {
    return (
        <div className={classNames(className, 'atl-flex atl-items-center')}>
            <Input
                type="toggle"
                data-testid="toggle-inactive-employees"
                checked={open}
                onChange={(event) => setOpen?.(event.currentTarget.checked)}
            />
            <label className="atl-ml-8">
                <FormattedMessage id="text_include_inactive_employees" />
            </label>
        </div>
    );
}
